import React, { createContext, useState, useContext, useEffect } from "react";
import {
  login as authLogin,
  logout as authLogout,
  getCurrentUser,
  register as authRegister,
} from "../api/services/authService";
import axiosInstance from "../api/axiosConfig";
import { clearAllCachedData } from "../utils/stateRefresh";
import { getCountryCurrency } from "../utils/formatters";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(getCurrentUser());
  const [loading, setLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Force refresh function to trigger re-render
  const forceRefresh = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  // Auto-detect and set currency based on user's country
  const autoDetectCurrency = (user) => {
    if (user && user.country) {
      const detectedCurrency = getCountryCurrency(
        user.country,
        user.countryCode || ""
      );

      // Set currency in localStorage
      localStorage.setItem("currency", detectedCurrency.code);
      localStorage.setItem("currencySymbol", detectedCurrency.symbol);

      console.log(
        `Auto-detected currency: ${detectedCurrency.code} (${detectedCurrency.symbol}) for country: ${user.country}`
      );
    }
  };

  // Verify token and load user data on mount and after refresh
  useEffect(() => {
    const verifyToken = async () => {
      const token = localStorage.getItem("token");
      if (token) {
        try {
          // Set the token in axios headers
          axiosInstance.defaults.headers.common[
            "Authorization"
          ] = `Bearer ${token}`;
          const response = await axiosInstance.get("/auth/verify");
          if (response.data && response.data.user) {
            setUser(response.data.user);
            localStorage.setItem("user", JSON.stringify(response.data.user));
            // Auto-detect currency based on user's country
            autoDetectCurrency(response.data.user);
          } else {
            throw new Error("Invalid user data received");
          }
        } catch (error) {
          console.error("Token verification failed:", error);
          // Clear auth data on verification failure
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          setUser(null);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    };

    verifyToken();
  }, [refreshTrigger]);

  const login = async (email, password) => {
    try {
      // Clear any existing data before login
      setUser(null);

      const data = await authLogin(email, password);
      if (data && data.user) {
        setUser(data.user);
        // Set the token in axios headers after successful login
        axiosInstance.defaults.headers.common[
          "Authorization"
        ] = `Bearer ${data.token}`;

        // Auto-detect currency based on user's country
        autoDetectCurrency(data.user);

        // Force refresh to ensure all components re-render
        setTimeout(() => {
          forceRefresh();
        }, 100);
      }
      return data;
    } catch (error) {
      console.error("Login failed:", error);
      throw error;
    }
  };

  const register = async (userData) => {
    try {
      // Clear any existing data before registration
      setUser(null);

      const data = await authRegister(userData);
      
      // Save currency settings to localStorage after successful registration
      if (data && data.success && userData.country && userData.currency && userData.currencySymbol) {
        localStorage.setItem("country", userData.country);
        localStorage.setItem("countryCode", userData.countryCode || "");
        localStorage.setItem("currency", userData.currency);
        localStorage.setItem("currencySymbol", userData.currencySymbol);
        
        console.log(`Currency settings saved after registration: ${userData.currency} (${userData.currencySymbol}) for ${userData.country}`);
      }
      
      // Don't set user or token on registration - user needs to login separately
      return data;
    } catch (error) {
      console.error("Registration failed:", error);
      // Make sure we're always throwing an object with a message property
      if (error && typeof error === "object") {
        throw error;
      } else if (typeof error === "string") {
        throw { message: error };
      } else {
        throw { message: "Registration failed. Please try again." };
      }
    }
  };

  const logout = () => {
    authLogout();
    setUser(null);
    // Clear axios headers
    delete axiosInstance.defaults.headers.common["Authorization"];
    // Use utility to clear all cached data and refresh state
    clearAllCachedData();
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem("user", JSON.stringify(userData));
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        loading,
        updateUser,
        register,
        forceRefresh,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
